
  import { ENTITY_ITEMS_DROPDOWN_LIMIT } from '@/_legacy/constants';
  import { computed, defineComponent, inject, ref, Ref, watch } from 'vue';
  import FilterDateDropdown from '@/ui/components/analytics/FilterDateDropdown.vue';
  import { Icon } from '@/ui/components/icon/Icon';
  import { ReportFilter, ReportFilterDate, ReportWidget } from '@/services/api/resources/analytics-builder/model';
  import cloneDeep from 'lodash/cloneDeep';
  import { useFilterConfiguration, useFilters } from '@/ui/concerns/analytics/useFilters';
  import { TagField } from '@/ui/components/form/TagField';
  import { DropdownField } from '@/ui/components/form';
  import { Loader } from '@/ui/components/Loader';
  import { getDefaultFilterDate, recreateFilter } from '@/ui/concerns/analytics/useReportRecord';
  import { FilterType } from '@/services/api/resources/analytics-builder/constants';
  import { selectedWidgetFromAnalyticsPageKey } from '@/ui/injectionKeys';
  import { filterOptions } from '@/ui/helpers';
  import GuestFilters from '@/ui/components/analytics/sidebar/GuestFilters.vue';
  import { useReportInjection } from '@/ui/concerns/analytics/useReportInjection';
  import FilterSidebarActions from '@/ui/components/analytics/sidebar/FilterSidebarActions.vue';

  interface AnalyticsFiltersSidebarProps {
    isOpen: boolean;
    parentFilters?: ReportFilter | undefined;
    reportFilters: ReportFilter;
    disableAll: boolean;
  }

  type defaultFilters = 'folder' | 'entity' | 'group' | 'user' | 'category' | 'checklist' | 'punctuality';
  type optionalFilters = 'section' | 'formItem' | 'priority' | 'formStatus' | 'actionStatus' | 'creator';
  type numberOptionalFilters = Exclude<optionalFilters, 'formItem' | 'section' | 'priority'>;

  const MAX_LISTED_OPTIONS = 20;

  export default defineComponent<AnalyticsFiltersSidebarProps>({
    components: {
      GuestFilters,
      Loader,
      TagField,
      DropdownField,
      Icon,
      FilterDateDropdown,
      FilterSidebarActions,
    },
    props: ['isOpen', 'parentFilters', 'reportFilters', 'disableAll'],
    setup(props, context) {
      const { isFilterEnabled } = useFilterConfiguration();
      const { isGuest, reportId: id } = useReportInjection();
      const selectedWidget = inject<Ref<ReportWidget | null>>(selectedWidgetFromAnalyticsPageKey, ref(null));

      const filters = ref<ReportFilter>(cloneDeep(props.reportFilters));
      const formItemValue = ref<string[]>(filters.value.formItem ? [filters.value.formItem] : []);
      const selectedChecklists = ref<number[]>(filters.value.checklist.values.length > 0
        ? filters.value.checklist.values
        : (props.parentFilters?.checklist.values || []));

      const approvalDateValue = computed(() => filters.value.approvalDate ?? getDefaultFilterDate());
      const isWidgetFilters = computed<boolean>(() => !!props.parentFilters);

      const onDateInput = (date: ReportFilterDate) => {
        filters.value.date = date;
      };

      const onApprovalDateInput = (date: ReportFilterDate) => {
        filters.value.approvalDate = date;
      };

      const onDefaultInput = (filterType: Exclude<defaultFilters, 'punctuality'>, value: number[]) => {
        filters.value[filterType].values = value;
      };

      const onOptionalInput = (filterType: numberOptionalFilters, value: number[]) => {
        if (filterType in filters.value && filters.value[filterType] !== undefined) {
          filters.value[filterType]!.values = value;
          return;
        }

        filters.value[filterType] = { values: value };
      };

      const onSectionInput = (section: string[]) => {
        filters.value.section
          ? Object.assign(filters.value.section, { values: section })
          : Object.assign(filters.value, { section: { values: section } });
      };

      const onFormItemInput = (formItem: string[]) => {
        formItemValue.value = formItem;
      };

      const onApplyFilters = () => {
        if (formItemValue.value.length) {
          [filters.value.formItem] = formItemValue.value;
        } else if (filters.value.formItem) {
          filters.value.formItem = null;
        }

        context.emit('apply', filters.value);
      };

      const onClearAllFilters = () => {
        formItemValue.value = [];
        filters.value = recreateFilter(filters.value);
      };

      const {
        folders,
        entities,
        approvalStatuses,
        groups,
        users,
        categories,
        checklists,
        sections,
        formItems,
        punctuality,
        priority,
        formStatus,
        actionStatus,
        getFilters,
        getPublicFilters,
        getSectionFilter,
        getPublicSectionFilter,
        getFormItemFilter,
        getPublicFormItemFilter,
      } = useFilters(selectedChecklists);

      const getFiltersTask = computed(() => isGuest ? getPublicFilters(id.value!) : getFilters);
      const getSectionFilterTask = computed(() => isGuest ? getPublicSectionFilter(id.value!) : getSectionFilter);
      const getFormItemFilterTask = computed(() => isGuest
        ? getPublicFormItemFilter(id.value!, filters.value.formItem)
        : getFormItemFilter);

      const folderOptions = computed(() => filterOptions(folders.value, (props.parentFilters?.folder.values || [])));
      const entityOptions = computed(() => filterOptions(entities.value, (props.parentFilters?.entity.values || [])));
      const groupOptions = computed(() => filterOptions(groups.value, (props.parentFilters?.group.values || [])));
      const userOptions = computed(() => filterOptions(users.value, (props.parentFilters?.user.values || [])));
      const categoryOptions = computed(() => filterOptions(categories.value, (props.parentFilters?.category.values || [])));
      const checklistOptions = computed(() => filterOptions(checklists.value, (props.parentFilters?.checklist.values || [])));

      const isOptionalFilterVisible = (filterType: FilterType) => isWidgetFilters.value
        && isFilterEnabled(selectedWidget?.value?.metric, filterType);

      const isSectionFilterVisible = computed(() => isOptionalFilterVisible(FilterType.section));
      const isFormItemFilterVisible = computed(() => isOptionalFilterVisible(FilterType.formItem));
      const isPriorityFilterVisible = computed(() => isOptionalFilterVisible(FilterType.priority));
      const isFormStatusFilterVisible = computed(() => isOptionalFilterVisible(FilterType.formStatus));
      const isActionStatusFilterVisible = computed(() => isOptionalFilterVisible(FilterType.actionStatus));
      const isCreatorFilterVisible = computed(() => isOptionalFilterVisible(FilterType.creator));
      const isApprovalStatusFilterVisible = computed(() => isOptionalFilterVisible(FilterType.approvalStatus));
      const isApproversFilterVisible = computed(() => isOptionalFilterVisible(FilterType.approvers));

      const hasParentReportPunctualitySelected = computed(() => {
        return !!props.parentFilters?.punctuality;
      });

      watch(() => filters.value.checklist.values, () => {
        selectedChecklists.value = filters.value.checklist.values.length > 0
          ? filters.value.checklist.values
          : (props.parentFilters?.checklist.values || []);
      });

      watch(() => selectedChecklists.value, () => runOptionalFiltersTask());
      watch(() => filters.value.formItem, () => {
        if (!isGuest) {
          return;
        }

        if (filters.value.formItem) {
          getFormItemFilterTask.value.run();
        }
      });

      watch(() => props.isOpen, value => {
        if (value) {
          filters.value = cloneDeep(props.reportFilters);
          formItemValue.value = filters.value.formItem ? [filters.value.formItem] : [];
        }
      });

      const runOptionalFiltersTask = () => {
        getSectionFilterTask.value.run();
        getFormItemFilterTask.value.run();
      };

      runOptionalFiltersTask();

      return {
        isGuest,
        formItemValue,
        approvalDateValue,
        onDateInput,
        onApprovalDateInput,
        onDefaultInput,
        onOptionalInput,
        onSectionInput,
        onFormItemInput,
        onApplyFilters,
        onClearAllFilters,
        filters,
        getFiltersTask,
        getSectionFilterTask,
        folderOptions,
        entityOptions,
        groupOptions,
        userOptions,
        categoryOptions,
        checklistOptions,
        approvalStatuses,
        users,
        sections,
        formItems,
        punctuality,
        priority,
        formStatus,
        actionStatus,
        isOptionalFilterVisible,
        isSectionFilterVisible,
        isFormItemFilterVisible,
        isPriorityFilterVisible,
        isFormStatusFilterVisible,
        isActionStatusFilterVisible,
        isCreatorFilterVisible,
        isApprovalStatusFilterVisible,
        isApproversFilterVisible,
        hasParentReportPunctualitySelected,
        FilterType,
        MAX_LISTED_OPTIONS,
        ENTITY_ITEMS_DROPDOWN_LIMIT,
      };
    },
  });
